import React, { useEffect, useMemo, useState } from 'react';
import { buildToday, dueLabel, journeyIndex, nextStep, STAGES } from './deskModel';

const TONE_DOT = {
  action: 'var(--bd-mari)',
  late: 'var(--bd-red)',
  info: 'var(--bd-indigo)',
  won: 'oklch(0.52 0.12 155)',
};
const TONE_CHIP = { action: 'bd-chip--mari', late: 'bd-chip--red', info: 'bd-chip--indigo', won: 'bd-chip--green' };

export function OwnerAvatar({ email, team }) {
  if (!email) return <span className="bd-av bd-av--none" title="Unassigned" aria-label="Unassigned">?</span>;
  const person = team.find((t) => t.email === email);
  const name = person?.name || email.split('@')[0];
  return <span className="bd-av" title={`Owner: ${name}`} aria-label={`Owner ${name}`}>{name.charAt(0).toUpperCase()}</span>;
}

const SOURCE_LABEL = { shopify_company: 'Website', zoho_customer: 'Zoho' };

export function LeadRow({ lead, today, team, selected, onSelect, onAction }) {
  const step = nextStep(lead, today);
  const due = step.due || dueLabel(lead, today);
  const place = [lead.city, lead.country].filter(Boolean).join(', ');
  return (
    <div
      role="button"
      tabIndex={0}
      className="bd-row"
      aria-current={selected ? 'true' : undefined}
      onClick={() => onSelect(lead)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(lead); } }}
    >
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0 }}>
        <span className="bd-av" style={{ width: 38, height: 38, fontSize: 14, background: 'var(--bd-indigo-tint)', color: 'var(--bd-indigo-deep)' }} aria-hidden="true">
          {(lead.brandName || '?').charAt(0).toUpperCase()}
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
          <div className="bd-row-title">
            <strong style={{ fontSize: 15 }}>{lead.brandName}</strong>
            {place && <span style={{ fontSize: 13, color: 'var(--bd-muted)' }}>{place}</span>}
            {SOURCE_LABEL[lead.source] && <span className="bd-chip">{SOURCE_LABEL[lead.source]}</span>}
            {lead.keptOut ? <span className="bd-chip">Kept out</span> : <span className="bd-chip bd-chip--indigo">{STAGES[lead.stage]?.label || lead.stage}</span>}
            {lead.account?.totalSpent > 0 && <span className="bd-mono" style={{ fontSize: 12, color: 'var(--bd-muted)' }}>₹{Math.round(lead.account.totalSpent).toLocaleString('en-IN')}</span>}
          </div>
          <span className="bd-row-line">{step.text}</span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span className={`bd-mono bd-due--${due.tone}`} style={{ fontSize: 12 }}>{due.text}</span>
        <OwnerAvatar email={lead.owner} team={team} />
        {step.cta && !lead.keptOut && (
          <button
            type="button"
            className="bd-btn bd-btn--ink"
            onClick={(e) => { e.stopPropagation(); onAction(lead, step.tool); }}
          >
            {step.cta}
          </button>
        )}
      </div>
    </div>
  );
}

function LeadPreview({ lead, today, team, loadActivity, onOpen, onAction, onQuickSnooze }) {
  const [activity, setActivity] = useState([]);
  useEffect(() => {
    let live = true;
    setActivity([]);
    loadActivity(lead.id).then((list) => { if (live) setActivity(list.slice(0, 4)); }).catch(() => {});
    return () => { live = false; };
  }, [lead.id, loadActivity]);
  const step = nextStep(lead, today);
  const idx = journeyIndex(lead.stage);
  const owner = team.find((t) => t.email === lead.owner);
  return (
    <aside className="bd-side" aria-label={`Selected lead ${lead.brandName}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span className="bd-k">Selected</span>
        <h2 style={{ fontSize: 22, fontWeight: 800 }}>{lead.brandName}</h2>
        <span style={{ fontSize: 14, color: 'var(--bd-muted)' }}>
          {[lead.city, lead.country].filter(Boolean).join(', ') || 'Location unknown'} · Owner: {owner?.name || lead.owner || 'unassigned'}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div className="bd-journey" aria-label={`Stage ${idx + 1} of 8`}>
          {Array.from({ length: 8 }, (_, i) => <span key={i} className={i <= idx ? 'on' : ''} />)}
        </div>
        <span style={{ fontSize: 13, color: 'var(--bd-ink-2)' }}><strong>{STAGES[lead.stage]?.label}</strong>{lead.lastReplyAt ? ` · replied ${new Date(lead.lastReplyAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''}</span>
      </div>
      {step.cta && (
        <div className="bd-next">
          <span className="bd-k" style={{ color: 'var(--bd-mari-ink)' }}>Next step{step.due ? ` · ${step.due.text}` : ''}</span>
          <p>{step.text}</p>
          <button type="button" className="bd-btn bd-btn--ink" style={{ width: '100%' }} onClick={() => onAction(lead, step.tool)}>{step.cta}</button>
        </div>
      )}
      <div className="bd-grid2" style={{ gap: 8 }}>
        <button type="button" className="bd-btn bd-btn--ghost" onClick={() => onAction(lead, 'whatsapp')}>WhatsApp</button>
        <button type="button" className="bd-btn bd-btn--ghost" onClick={() => onAction(lead, 'call')}>Log call</button>
        <button type="button" className="bd-btn bd-btn--ghost" onClick={() => onQuickSnooze(lead)}>Snooze 7 days</button>
        <button type="button" className="bd-btn bd-btn--ghost" onClick={() => onOpen(lead)}>Open lead</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <span className="bd-k">Latest</span>
        {activity.length === 0 ? <span style={{ fontSize: 13, color: 'var(--bd-muted)' }}>No history yet.</span> : (
          <ul className="bd-timeline">
            {activity.map((a) => (
              <li key={a.id}>
                <span className="bd-dot" style={{ background: a.type === 'reply_received' ? 'var(--bd-mari)' : 'var(--bd-indigo)' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 14, lineHeight: 1.4 }}>{a.summary}</span>
                  <span className="bd-mono" style={{ fontSize: 11, color: 'var(--bd-muted)' }}>{a.atIso ? new Date(a.atIso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : ''}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

const RAIL = [
  { key: 'replied', label: 'Buyer replied', count: 'replied', tone: 'action' },
  { key: 'overdue', label: 'Overdue', count: 'overdue', tone: 'late' },
  { key: 'dueToday', label: 'Due today', count: 'dueToday', tone: 'info' },
  { key: 'pack', label: 'Swatch to pack', count: 'pack', tone: 'info' },
  { key: 'delivered', label: 'Delivered, call now', count: 'delivered', tone: 'action' },
  { key: 'winback', label: 'Win-back', count: 'winback', tone: 'won' },
];

export default function TodayView({ leads, today, team, me, ownerFilter, onOwnerFilter, loadActivity, onOpen, onAction, onQuickSnooze, searchResults }) {
  const [focus, setFocus] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const { groups, counts, total } = useMemo(() => buildToday(leads, { today, ownerFilter, me }), [leads, today, ownerFilter, me]);
  const shown = searchResults
    ? [{ key: 'search', title: `Search results`, hint: `${searchResults.length} found`, tone: 'info', items: searchResults.slice(0, 50) }]
    : focus === 'all' ? groups : groups.filter((g) => g.key === focus);
  const flat = shown.flatMap((g) => g.items);
  const selected = flat.find((l) => l.id === selectedId) || flat[0] || null;
  const firstName = (me?.email ? (team.find((t) => t.email === me.email)?.name || me.email.split('@')[0]) : '').split(/\s+/)[0];

  return (
    <div className="bd-today">
      <nav className="bd-rail" aria-label="Queues">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="bd-k" style={{ padding: '0 12px 6px' }}>Today</span>
          <button type="button" className="bd-queue" aria-pressed={focus === 'all'} onClick={() => setFocus('all')}>
            <span>Everything due</span><span className="bd-mono" style={{ fontSize: 12, fontWeight: 600 }}>{total}</span>
          </button>
          {RAIL.map((q) => (
            <button key={q.key} type="button" className="bd-queue" aria-pressed={focus === q.key} onClick={() => setFocus(q.key)}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span className="bd-dot" style={{ background: TONE_DOT[q.tone] }} />{q.label}</span>
              <span className="bd-mono" style={{ fontSize: 12, fontWeight: 600 }}>{counts[q.count]}</span>
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="bd-k" style={{ padding: '0 12px 6px' }}>Team</span>
          <button type="button" className="bd-queue" aria-pressed={ownerFilter === 'unassigned'} onClick={() => onOwnerFilter(ownerFilter === 'unassigned' ? 'all' : 'unassigned')}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span className="bd-dot" style={{ background: 'oklch(0.6 0.02 275)' }} />Unassigned</span>
            <span className="bd-mono" style={{ fontSize: 12, fontWeight: 600 }}>{counts.unassigned}</span>
          </button>
          <div className="bd-queue" style={{ cursor: 'default' }}><span>Snoozed</span><span className="bd-mono" style={{ fontSize: 12 }}>{counts.snoozed}</span></div>
          <div className="bd-queue" style={{ cursor: 'default' }}><span>Lost</span><span className="bd-mono" style={{ fontSize: 12 }}>{counts.lost}</span></div>
        </div>
      </nav>

      <main className="bd-main">
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <h1 style={{ fontSize: 28, fontWeight: 800 }}>
              {searchResults ? 'Search' : total ? `${firstName ? `${firstName}, ` : ''}${total} thing${total === 1 ? '' : 's'} need${total === 1 ? 's' : ''} you today.` : 'All clear for today.'}
            </h1>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--bd-muted)' }}>
              Sorted by what loses a buyer fastest: replies first, then deliveries, then everything due.
            </p>
          </div>
        </div>
        {shown.length === 0 && (
          <div className="bd-empty">
            <strong style={{ color: 'var(--bd-ink)' }}>Nothing waiting here.</strong>
            <span>New replies and due follow-ups appear on their own. Check Accounts for buyers to win back.</span>
          </div>
        )}
        {shown.map((g) => (
          <section key={g.key} className="bd-group" aria-label={g.title}>
            <div className="bd-group-head">
              <h2 style={{ fontSize: 15, fontWeight: 700 }}>{g.title}</h2>
              <span className={`bd-chip ${TONE_CHIP[g.tone] || ''}`}>{g.items.length}</span>
              <span style={{ fontSize: 13, color: 'var(--bd-muted)' }}>{g.hint}</span>
            </div>
            <div className="bd-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {g.items.map((lead) => (
                <LeadRow key={lead.id} lead={lead} today={today} team={team} selected={selected?.id === lead.id} onSelect={(l) => setSelectedId(l.id)} onAction={onAction} />
              ))}
            </div>
          </section>
        ))}
      </main>

      {selected && (
        <LeadPreview lead={selected} today={today} team={team} loadActivity={loadActivity} onOpen={onOpen} onAction={onAction} onQuickSnooze={onQuickSnooze} />
      )}
    </div>
  );
}
